/**
 * VideoComponent
 * 
 * Individual video item component that handles:
 * - Thumbnail display before initialization
 * - Loading state during video initialization
 * - Encrypted video playback after initialization
 * 
 * Matches Flutter ContentListWidget's video item behavior:
 * 1. Shows thumbnail (clickable)
 * 2. On click, initializes video (shows loading overlay)
 * 3. On success, shows EncryptedVideoPlayer
 * 
 * Video Management:
 * - Dispatches playback state to Redux
 * - Responds to Redux state for fullscreen mode
 * - Enables external control of playback
 * 
 * This is a pure video player component - for content with UI elements,
 * use ContentComponent from presentation/feature/content/components/
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '@/presentation/store';
import {
  updatePlaybackState,
  requestPlay,
  requestPause,
} from '@/presentation/store/slices/videoManagement/videoManagementSlice';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { EncryptedVideoPlayer } from './player/EncryptedVideoPlayer';
import { ContentPreview } from '@/presentation/feature/content/components/ContentPreview';
import { isMobileViewport, isLandscapeViewport } from '@/presentation/responsiveDesign/responsiveDesign';

export interface VideoComponentProps {
  /** Screen key that will be used for video management and identification */
  screenKey: string;

  /** Media entity containing video information */
  media: UserMediaEntity;

  /** Whether to autoplay after initialization */
  autoplay?: boolean;

  /** Whether to mute video */
  muted?: boolean;

  /** Custom CSS class */
  className?: string;

  /** Whether video is currently in fullscreen mode */
  isFullscreen?: boolean;

  /**
   * Optional custom thumbnail node shown (clickable) until playback starts,
   * replacing the default `ContentPreview` that loads `media`'s thumbnail URL.
   *
   * Needed when the thumbnail is NOT a plain CDN URL — e.g. encrypted chat /
   * Community media, whose thumbnail is E2EE and must be rendered via
   * `EncryptedCachedImage`. When omitted, the default URL-based preview is used
   * (unchanged user-gen behaviour).
   */
  thumbnail?: React.ReactNode;

  /** Callback when video is clicked (before initialization) */
  onClick?: (media: UserMediaEntity) => void;

  /** Callback when video initialization starts */
  onInitStart?: (media: UserMediaEntity) => void;

  /** Callback when video is ready to play */
  onReady?: (media: UserMediaEntity) => void;

  /** Callback when video playback actually starts */
  onPlaybackStart?: (media: UserMediaEntity) => void;

  /** Callback when fullscreen button is clicked */
  onFullscreenClick?: (media: UserMediaEntity) => void;

  /** Callback when video element is ready - for fullscreen overlay */
  onVideoElementReady?: (media: UserMediaEntity, videoElement: HTMLVideoElement) => void;

  /** Callback when video state changes - for fullscreen overlay */
  onVideoFullScreenStateChange?: (media: UserMediaEntity, state: {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
  }) => void;

  /** Callback on error */
  onError?: (media: UserMediaEntity, error: Error) => void;
}

export const VideoComponent: React.FC<VideoComponentProps> = ({
  screenKey,
  media,
  autoplay = false,
  muted = false,
  className = '',
  isFullscreen = false,
  thumbnail,
  onClick,
  onReady,
  onPlaybackStart,
  onFullscreenClick,
  onVideoElementReady,
  onVideoFullScreenStateChange,
  onError
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  // Compute video key from screenKey and media ID
  const videoKey = useMemo(() => MediaUtils.composeVideoPlayerKey(screenKey, media.id), [screenKey, media.id]);

  // Video control state
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted] = useState(muted);

  // Extract duration and typeLimit from media entity (convert from milliseconds to seconds)
  const duration = media.duration ? media.duration / 1000 : 0;

  // Get video state from Redux using videoKey
  const videoState = useSelector((state: RootState) => state.video.videos[videoKey]);

  // Derive state
  const isInitialized = videoState && !videoState.isLoading && !videoState.error &&
    !videoState.isDisposed && videoState.initializedAt > 0;
  const isLoading = videoState?.isLoading || false;
  const error = videoState?.error || null;
  const isDisposed = videoState?.isDisposed || false;

  // Get thumbnail URL
  const thumbnailUrl = MediaUtils.getMediaThumbnailUrl(media);

  // Cleanup when video is marked as disposed
  useEffect(() => {
    if (isDisposed) {
      console.log(`🗑️ [VideoComponent] Video ${videoKey} marked as disposed, cleaning up`);

      // Clear video element reference
      setVideoElement(null);

      // Reset playback state
      setHasStartedPlaying(false);
      setIsPlaying(false);
    }
  }, [isDisposed, videoKey]);

  /**
   * Handle video click - just notify parent
   */
  const handleVideoClick = async () => {
    if (isInitialized || isLoading) return;

    // Notify parent - parent will handle initialization
    onClick?.(media);
  };

  const handlePlayerReady = useCallback(() => {
    onReady?.(media);
  }, [onReady, media]);

  const handlePlayerError = useCallback((playerError: Error) => {
    onError?.(media, playerError);
  }, [onError, media]);

  const handlePlaybackStarted = useCallback(() => {
    // Mark that video has actually started playing
    setHasStartedPlaying(true);
    onPlaybackStart?.(media);
  }, [media, onPlaybackStart]);

  // Video control methods
  const handleVideoElementReady = useCallback((element: HTMLVideoElement) => {
    setVideoElement(element);

    // Register with global registry for external control
    videoElementRegistry.register(videoKey, element);

    onVideoElementReady?.(media, element);
  }, [videoKey, media, onVideoElementReady]);

  // Note: videoElementRegistry.unregister is handled by:
  // - Manual disposal: disposeVideoInternal in useVideoManagement
  // - Navigation: EncryptedVideoPlayer's cleanup detaches media but preserves HLS

  // Apply fullscreen video sizing based on aspect ratio
  useEffect(() => {
    if (!videoElement) return;

    if (!isFullscreen) {
      // Reset to default list view sizing - let CSS handle it
      videoElement.style.width = '';
      videoElement.style.height = '';
      videoElement.style.objectFit = 'cover';
      return;
    }

    // Fullscreen mode - fill container (overlay handles viewport sizing dynamically)
    videoElement.style.width = '100%';
    videoElement.style.height = '100dvh';
    videoElement.style.objectFit = 'contain';
  }, [videoElement, isFullscreen, media.aspectRatio]);

  const handlePlayPause = useCallback(() => {
    if (videoElement) {
      if (isPlaying) {
        dispatch(requestPause({ videoKey, forced: true }));
        videoElementRegistry.pause(videoKey);
        return;
      }

      dispatch(requestPlay({ videoKey }));
      videoElementRegistry.play(videoKey);
    }
  }, [videoElement, dispatch, isPlaying, videoKey]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Update Redux playback state whenever local state changes
  useEffect(() => {
    if (videoElement && isInitialized) {
      const currentTime = videoElement.currentTime;
      dispatch(updatePlaybackState({
        videoKey,
        playbackState: {
          isPlaying,
          currentTime,
          duration,
          volume,
          isMuted
        }
      }));
    }
  }, [videoElement, isInitialized, videoKey, isPlaying, duration, volume, isMuted, dispatch]);

  // Notify parent of video state changes
  useEffect(() => {
    if (onVideoFullScreenStateChange && videoElement) {
      const currentTime = videoElement.currentTime;
      onVideoFullScreenStateChange(media, {
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted
      });
    }
  }, [onVideoFullScreenStateChange, media, videoElement, isPlaying, duration, volume, isMuted]);

  // Notify parent of errors
  useEffect(() => {
    if (error) {
      onError?.(media, new Error(error));
    }
  }, [error, media, onError]);

  // Keyboard shortcuts (only when video is initialized and has focus)
  useEffect(() => {
    if (!videoElement || !isInitialized) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if the video or its container is focused or if no input is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputFocused) return;

      // Use e.code for physical key position (keyboard layout independent)
      // Use e.key for special keys like Space
      switch (e.code) {
        case 'Space': // Spacebar - play/pause (same on all keyboards)
          e.preventDefault();
          handlePlayPause();
          break;
        case 'KeyF': // F key physical position - toggle fullscreen (same position on all keyboards)
          if (onFullscreenClick) {
            e.preventDefault();
            onFullscreenClick(media);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [videoElement, isInitialized, handlePlayPause, onFullscreenClick, media]);

  // Calculate container styles based on viewport and fullscreen state
  const getContainerStyle = (): React.CSSProperties => {
    if (isFullscreen) {
      // Fullscreen: fill entire space, video element uses objectFit: contain for aspect ratio
      return {
        width: '100%',
        height: '100%',
      };
    }

    // Non-fullscreen: Full width container for all content (description, actions, etc)
    return {
      width: '100%',
    };
  };

  // Calculate video/thumbnail wrapper styles
  const getVideoWrapperStyle = (): React.CSSProperties => {
    if (isFullscreen) {
      // Fullscreen: no constraints
      return {
        width: '100%',
        height: '100%',
      };
    }

    // Non-fullscreen: Use container queries approach
    // Set width based on aspect ratio, constrained by maxWidth
    // Height will be calculated from width via aspect-ratio
    // This works better than height-first approach for maintaining aspect ratio on resize

    // If video is portrait, set height to max 85vh. Width will adjust via aspect ratio.
    // If video is landscape, set width to 100% of container. Height will adjust via aspect ratio.
    const videoIsLandscape = MediaUtils.isLandscapeVideo(media);
    const deviceIsMobile = isMobileViewport();
    const deviceIsLandscape = isLandscapeViewport();
    let aspectRatio;
    if (videoIsLandscape) {
      // video aspect ratio for mobile portrait, else use 21:9 to prevent overly tall video on desktop or on mobile landscape
      aspectRatio = deviceIsMobile && !deviceIsLandscape ? media.aspectRatio.toString() : '21/9';
      return {
        width: '100%', // Take full width of parent
        maxWidth: '100%', // No max width for landscape
        aspectRatio: aspectRatio,
        margin: '0 auto',
        position: 'relative',
      };
    } else {
      // 3 /4 for mobile to prevent overly tall video, else use actual aspect ratio for desktop
      aspectRatio = deviceIsMobile ? '3/4' : media.aspectRatio.toString();
      return {
        // For portrait, limit height to 85vh
        width: '100%', // Take full width of parent
        maxWidth: `calc(85vh * ${aspectRatio})`, // Max width based on max height
        aspectRatio: aspectRatio,
        margin: '0 auto',
        position: 'relative',
      };
    }
  };

  return (
    <div
      className={`relative ${className}`}
      style={getContainerStyle()}
    >
      {/* Video/Thumbnail Container - Single wrapper for proper overlapping */}
      <div
        className="relative"
        style={getVideoWrapperStyle()}
      >
        {/* Video Player - Render when initialized and not disposed */}
        {isInitialized && !isDisposed && (
          <div
            className="absolute inset-0 w-full h-full"
            style={{ cursor: isFullscreen ? 'inherit' : 'pointer' }}
            data-video-container={media.id}
            onClick={(e) => {
              // Only toggle play/pause if clicking the video itself (not controls)
              const target = e.target as HTMLElement;
              const isControlClick = target.closest('.video-controls') !== null ||
                target.tagName === 'BUTTON' ||
                target.tagName === 'INPUT';


              if (!isControlClick && (target.tagName === 'VIDEO')) {
                handlePlayPause();
              }
            }}
          >

            <EncryptedVideoPlayer
              screenKey={screenKey}
              videoId={media.id}
              videoUrl={videoState.videoUrl}
              decryptionKeys={videoState.decryptionKeys}
              autoplay={autoplay}
              muted={muted}
              className="absolute inset-0 z-10"
              onReady={handlePlayerReady}
              onPlay={handlePlay}
              onPause={handlePause}
              onPlaybackStart={handlePlaybackStarted}
              onError={handlePlayerError}
              onVideoElementReady={handleVideoElementReady}
              onVolumeChange={setVolume}
            />
          </div>
        )}

        {/* Thumbnail Overlay - Show until video starts playing - positioned absolutely over video */}
        {!hasStartedPlaying && (
          <div className="absolute inset-0 z-30">
            {thumbnail ? (
              // Custom thumbnail node (e.g. encrypted-media `EncryptedCachedImage`).
              // Kept clickable to trigger init when not already loading/initialized,
              // mirroring `ContentPreview`'s `isClickable` gating.
              <div
                className="w-full h-full"
                style={{ cursor: (!isLoading && !isInitialized && !error) ? 'pointer' : 'default' }}
                onClick={(!isLoading && !isInitialized && !error) ? handleVideoClick : undefined}
              >
                {thumbnail}
              </div>
            ) : (
              <ContentPreview
                thumbnailUrl={thumbnailUrl}
                alt={media.description || 'Video thumbnail'}
                isClickable={!isLoading && !isInitialized && !error}
                onClick={handleVideoClick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
